import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';


const HOVER_DURATION = 500;


/**
 * As Seen In Logo Marquee
 *
 * Example:
 *
 * Healthline
 * → Body & Soul
 * → Men's Health
 * → Independent
 * → Stuff
 * → Healthline
 * → Body & Soul
 * → ...
 *
 * Features:
 *
 * - Fills the full viewport
 * - No blank section
 * - Equal logo spacing
 * - Exact logo order
 * - Perfect infinite loop
 * - Smooth stop on hover
 * - Smooth resume
 * - Responsive rebuild
 */
class AsSeenInComponent extends Component {

  viewport = null;

  track = null;

  content = null;

  originalSequence = null;

  viewportWidth = 0;

  rateAnimation = null;

  buildId = 0;


  connectedCallback() {

    super.connectedCallback();


    this.viewport =
      this.querySelector(
        '.as-seen-in__viewport'
      );


    this.track =
      this.querySelector(
        '.as-seen-in__track'
      );


    this.content =
      this.querySelector(
        '.as-seen-in__content:not([data-as-seen-clone])'
      );


    this.originalSequence =
      this.content?.querySelector(
        '.as-seen-in__sequence:not([data-as-seen-repeat])'
      );


    if (
      !this.viewport ||
      !this.track ||
      !this.content ||
      !this.originalSequence
    ) {
      return;
    }


    /*
     * Remove anything generated during
     * a previous Theme Editor render.
     */
    this.cleanupGenerated();


    /*
     * Register interactions.
     */
    this.setupEvents();


    /*
     * Build immediately.
     */
    this.build();


    /*
     * Logo dimensions may change slightly
     * after images are loaded.
     */
    const images =
      this.originalSequence
        .querySelectorAll('img');


    images.forEach((image) => {

      if (
        image.complete
      ) {
        return;
      }


      image.addEventListener(
        'load',
        this.handleImageLoad,
        {
          once: true
        }
      );

    });

  }


  disconnectedCallback() {

    this.rateAnimation?.cancel();


    window.removeEventListener(
      'resize',
      this.handleResize
    );


    this.removeEventListener(
      'pointerenter',
      this.pauseSmoothly
    );


    this.removeEventListener(
      'pointerleave',
      this.resumeSmoothly
    );


    this.removeEventListener(
      'focusin',
      this.pauseSmoothly
    );


    this.removeEventListener(
      'focusout',
      this.handleFocusOut
    );


    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );


    super.disconnectedCallback?.();

  }


  /**
   * Setup events.
   */
  setupEvents() {

    window.addEventListener(
      'resize',
      this.handleResize
    );


    /*
     * Smooth stop on hover.
     */
    this.addEventListener(
      'pointerenter',
      this.pauseSmoothly
    );


    /*
     * Smooth resume.
     */
    this.addEventListener(
      'pointerleave',
      this.resumeSmoothly
    );


    /*
     * Accessibility.
     */
    this.addEventListener(
      'focusin',
      this.pauseSmoothly
    );


    this.addEventListener(
      'focusout',
      this.handleFocusOut
    );


    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

  }


  /**
   * Remove generated repeated sequences
   * and duplicated content.
   */
  cleanupGenerated() {

    if (
      !this.track ||
      !this.content
    ) {
      return;
    }


    this.track
      .querySelectorAll(
        '[data-as-seen-clone]'
      )
      .forEach((element) => {

        element.remove();

      });


    this.content
      .querySelectorAll(
        '[data-as-seen-repeat]'
      )
      .forEach((element) => {

        element.remove();

      });

  }


  /**
   * Build full logo marquee.
   */
  async build() {

    if (
      !this.viewport ||
      !this.track ||
      !this.content ||
      !this.originalSequence
    ) {
      return;
    }


    const currentBuild =
      ++this.buildId;


    /*
     * Stop animation while rebuilding.
     */
    this.removeAttribute(
      'data-ready'
    );


    this.rateAnimation?.cancel();

    this.rateAnimation = null;


    this.cleanupGenerated();


    /*
     * Wait for browser layout.
     */
    await nextFrame();


    if (
      currentBuild !==
        this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    const viewportWidth =
      this.viewport
        .getBoundingClientRect()
        .width;


    const sequenceWidth =
      this.originalSequence
        .getBoundingClientRect()
        .width;


    if (
      viewportWidth <= 0 ||
      sequenceWidth <= 0
    ) {
      return;
    }


    this.viewportWidth =
      viewportWidth;


    /*
     * Fill slightly MORE than screen width.
     *
     * This prevents any empty area.
     */
    const minimumWidth =
      viewportWidth * 1.25;


    /*
     * Example:
     *
     * Full logo sequence width = 600
     * screen width = 1600
     *
     * ceil(2000 / 600)
     * = 4 sequences
     *
     * Healthline
     * Body&Soul
     * Men's Health
     * Independent
     * Stuff
     *
     * repeated 4 times.
     */
    const copies =
      Math.max(
        1,
        Math.ceil(
          minimumWidth /
          sequenceWidth
        )
      );


    /*
     * Add complete sequences.
     *
     * Correct:
     *
     * 1 2 3 4 5
     * 1 2 3 4 5
     * 1 2 3 4 5
     *
     * NOT:
     *
     * 1 1 1
     * 2 2 2
     */
    for (
      let index = 1;
      index < copies;
      index++
    ) {

      const sequence =
        this.originalSequence
          .cloneNode(true);


      sequence.setAttribute(
        'data-as-seen-repeat',
        ''
      );


      sequence.setAttribute(
        'aria-hidden',
        'true'
      );


      this.prepareClone(
        sequence
      );


      this.content.appendChild(
        sequence
      );

    }


    await nextFrame();


    if (
      currentBuild !==
        this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    /*
     * Duplicate the ENTIRE filled content.
     *
     * A:
     *
     * 1 2 3 4 5
     * 1 2 3 4 5
     * 1 2 3 4 5
     *
     * B:
     *
     * 1 2 3 4 5
     * 1 2 3 4 5
     * 1 2 3 4 5
     *
     * Track:
     *
     * A | B
     */
    const contentClone =
      this.content
        .cloneNode(true);


    contentClone.setAttribute(
      'data-as-seen-clone',
      ''
    );


    contentClone.setAttribute(
      'aria-hidden',
      'true'
    );


    this.prepareClone(
      contentClone
    );


    this.track.appendChild(
      contentClone
    );


    /*
     * Calculate duration based on
     * completed content width.
     */
    const completeWidth =
      this.content
        .getBoundingClientRect()
        .width;


    const baseSpeed =
      Number(
        this.getAttribute(
          'data-speed'
        )
      ) || 15;


    const widthRatio =
      Math.max(
        1,
        completeWidth /
        viewportWidth
      );


    const duration =
      baseSpeed *
      widthRatio;


    this.style.setProperty(
      '--as-seen-speed',
      `${duration}s`
    );


    await nextFrame();


    if (
      currentBuild !==
        this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    /*
     * Everything is loaded.
     * Start animation.
     */
    this.setAttribute(
      'data-ready',
      ''
    );


    requestAnimationFrame(
      () => {

        const animation =
          this.getAnimation();


        if (!animation) {
          return;
        }


        animation.updatePlaybackRate(
          this.matches(':hover')
            ? 0
            : 1
        );

      }
    );

  }


  /**
   * Prepare generated content.
   *
   * @param {HTMLElement} container
   */
  prepareClone(container) {

    /*
     * Remove duplicated IDs.
     */
    if (
      container.hasAttribute(
        'id'
      )
    ) {

      container.removeAttribute(
        'id'
      );

    }


    container
      .querySelectorAll('[id]')
      .forEach((element) => {

        element.removeAttribute(
          'id'
        );

      });


    /*
     * Generated logos should not duplicate
     * keyboard navigation.
     */
    container
      .querySelectorAll(
        `
          a,
          button,
          input,
          select,
          textarea,
          [tabindex]
        `
      )
      .forEach((element) => {

        element.setAttribute(
          'tabindex',
          '-1'
        );

      });

  }


  /**
   * Return CSS marquee animation.
   */
  getAnimation() {

    if (!this.track) {
      return null;
    }


    return (
      this.track
        .getAnimations()[0] ??
      null
    );

  }


  /**
   * Smooth stop.
   */
  pauseSmoothly = () => {

    const animation =
      this.getAnimation();


    if (!animation) {
      return;
    }


    animation.play();


    const from =
      this.rateAnimation?.current ??
      animation.playbackRate ??
      1;


    this.rateAnimation?.cancel();


    this.rateAnimation =
      animateValue({

        from,

        to: 0,

        duration:
          HOVER_DURATION,

        onUpdate:
          (value) => {

            animation.updatePlaybackRate(
              Math.max(
                0,
                value
              )
            );

          },

        onComplete:
          () => {

            animation.updatePlaybackRate(
              0
            );


            this.rateAnimation =
              null;

          },

      });

  };


  /**
   * Smooth resume.
   */
  resumeSmoothly = () => {

    if (
      document.hidden
    ) {
      return;
    }


    const animation =
      this.getAnimation();


    if (!animation) {
      return;
    }


    animation.play();


    const from =
      this.rateAnimation?.current ??
      animation.playbackRate ??
      0;


    this.rateAnimation?.cancel();


    this.rateAnimation =
      animateValue({

        from,

        to: 1,

        duration:
          HOVER_DURATION,

        onUpdate:
          (value) => {

            animation.updatePlaybackRate(
              Math.min(
                1,
                Math.max(
                  0,
                  value
                )
              )
            );

          },

        onComplete:
          () => {

            animation.updatePlaybackRate(
              1
            );


            this.rateAnimation =
              null;

          },

      });

  };


  /**
   * Recalculate after logo image loads.
   */
  handleImageLoad = () => {

    this.build();

  };


  /**
   * Rebuild after horizontal resize.
   */
  handleResize = debounce(
    () => {

      if (!this.viewport) {
        return;
      }


      const newWidth =
        this.viewport
          .getBoundingClientRect()
          .width;


      if (
        Math.abs(
          newWidth -
          this.viewportWidth
        ) < 2
      ) {
        return;
      }


      this.build();

    },
    250
  );


  /**
   * Keyboard focus handling.
   */
  handleFocusOut = (
    event
  ) => {

    if (
      event.relatedTarget &&
      this.contains(
        event.relatedTarget
      )
    ) {
      return;
    }


    this.resumeSmoothly();

  };


  /**
   * Browser visibility.
   */
  handleVisibilityChange =
    () => {

      const animation =
        this.getAnimation();


      if (!animation) {
        return;
      }


      if (
        document.hidden
      ) {

        animation.pause();

        return;

      }


      animation.play();


      animation.updatePlaybackRate(
        this.matches(':hover')
          ? 0
          : 1
      );

    };

}


/**
 * One animation frame.
 */
function nextFrame() {

  return new Promise(
    (resolve) => {

      requestAnimationFrame(
        () => resolve()
      );

    }
  );

}


/**
 * Smooth playback rate animation.
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) =>
    t * t * (3 - 2 * t),
  onComplete,
}) {

  const startTime =
    performance.now();


  let cancelled =
    false;


  let currentValue =
    from;


  function frame(
    currentTime
  ) {

    if (cancelled) {
      return;
    }


    const elapsed =
      currentTime -
      startTime;


    const progress =
      Math.min(
        elapsed /
        duration,
        1
      );


    const easedProgress =
      easing(
        progress
      );


    currentValue =
      from +
      (to - from) *
        easedProgress;


    onUpdate(
      currentValue
    );


    if (
      progress < 1
    ) {

      requestAnimationFrame(
        frame
      );

    } else {

      onComplete?.();

    }

  }


  requestAnimationFrame(
    frame
  );


  return {

    get current() {

      return currentValue;

    },


    cancel() {

      cancelled =
        true;

    },

  };

}


if (
  !customElements.get(
    'as-seen-in-component'
  )
) {

  customElements.define(
    'as-seen-in-component',
    AsSeenInComponent
  );

}